import { getOwner } from '@ember/application';
import Service from '@ember/service';
import { captureException, captureMessage, withScope } from '@sentry/ember';
import CatalogError from '@square/dashboard-data/utils/catalog/catalog-error';
import { isStatusCodeToIgnore } from '@square/dashboard-items-data/utils/sentry';
export default class CatalogSentry extends Service {
  captureException(...args) {
    const [exception, captureContext] = args;
    const xhrError = this.getXhrError(exception, captureContext);
    if (xhrError && isStatusCodeToIgnore(xhrError.xhrStatus ?? xhrError.status)) {
      return;
    }
    if (this.isProduction) {
      captureException(...args);
      return;
    }
    if (this.isStaging) {
      captureException(...args);
    }
    if (this.isTest) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error('[SENTRY]', exception, '\nDetails', captureContext);
  }
  captureMessage(...args) {
    if (this.isProduction) {
      captureMessage(...args);
      return;
    }
    if (this.isStaging) {
      captureMessage(...args);
    }
    if (this.isTest) {
      return;
    }

    // eslint-disable-next-line no-console
    console.error('[SENTRY]', args[0]);
  }
  withScope(callback) {
    if (this.isProduction || this.isStaging) {
      withScope(callback);
    } else {
      // Create a mock scope for local development that logs to console
      const mockScope = {
        setExtra: (key, value) => {
          // eslint-disable-next-line no-console
          console.log(`[SENTRY] setExtra: ${key}`, value);
        },
        setTag: (key, value) => {
          // eslint-disable-next-line no-console
          console.log(`[SENTRY] setTag: ${key}`, value);
        },
        setLevel: level => {
          // eslint-disable-next-line no-console
          console.log(`[SENTRY] setLevel: ${level}`);
        }
      };
      callback(mockScope);
    }
  }
  get isProduction() {
    return getOwner(this).resolveRegistration('config:environment').environment === 'production';
  }
  get isStaging() {
    return getOwner(this).resolveRegistration('config:environment').environment === 'staging';
  }
  get isTest() {
    return getOwner(this).resolveRegistration('config:environment').environment === 'test';
  }

  /*
   There are 2 ways of the CatalogSentry usage existing:
   1. this.catalogSentry.captureException(error, null);
   2. this.catalogSentry.withScope((scope) => {
      scope.setExtra('errors', errors);
      this.catalogSentry.captureException(new Error('Advanced Categories: some error message'));
    });
   and the "error" or "errors" can be original xhr or catalogError
  */
  getXhrError(exception, captureContext) {
    if (this.isCatalogErrorOrOriginalXhrError(exception)) {
      return exception;
    }

    // Handle if captureContext is an object (not a scope callback)
    if (captureContext && typeof captureContext === 'object' && 'extra' in captureContext) {
      const errors = captureContext.extra?.errors;
      if (this.isCatalogErrorOrOriginalXhrError(errors)) {
        return errors;
      }
      if (Array.isArray(errors) && errors.length > 0) {
        const firstError = errors[0];
        if (this.isCatalogErrorOrOriginalXhrError(firstError)) {
          return firstError;
        }
      }
    }
    return null;
  }
  isCatalogErrorOrOriginalXhrError(error) {
    return error instanceof CatalogError && typeof error.xhrStatus === 'number' || typeof error?.status === 'number' && typeof error?.url === 'string';
  }
}